import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import LoadErrorScreen from './LoadErrorScreen';

const API = process.env.REACT_APP_SERVER_URL;
const BEAR_PAW_AVATAR = `${process.env.PUBLIC_URL || ''}/bear-paw.svg`;

const brandButtonSx = {
  backgroundColor: '#8B7D6B',
  color: '#fff',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#7A6D5F' },
};

const PASSPORT_STATUS_LABELS = {
  not_submitted: 'Паспортные данные не заполнены',
  pending: 'Паспортные данные на проверке',
  approved: 'Паспортные данные подтверждены',
  rejected: 'Паспортные данные отклонены',
};

const emptyPassport = () => ({
  fullName: '',
  passportSeries: '',
  passportNumber: '',
  issueDate: '',
  issuedBy: '',
  registrationAddress: '',
  dateOfBirth: '',
});

const emptyPayout = () => ({
  recipientFullName: '',
  phoneForTransfer: '',
  cardNumber: '',
  banks: '',
});

function getPassportSeries(pd) {
  return String(pd?.passportSeries ?? pd?.series ?? '').trim();
}

function getPassportNumber(pd) {
  return String(pd?.passportNumber ?? pd?.number ?? '').trim();
}

function normalizePassportFromUser(pd = {}) {
  return {
    fullName: String(pd.fullName || '').trim(),
    passportSeries: getPassportSeries(pd),
    passportNumber: getPassportNumber(pd),
    issueDate: String(pd.issueDate ?? pd.issuedDate ?? '').trim(),
    issuedBy: String(pd.issuedBy || '').trim(),
    registrationAddress: String(pd.registrationAddress || '').trim(),
    dateOfBirth: String(pd.dateOfBirth || '').trim(),
  };
}

const emptyBlockFeedback = () => ({ type: '', message: '' });

const SectionAlert = ({ feedback, onClose }) => {
  if (!feedback?.message) return null;
  return (
    <Alert severity={feedback.type} sx={{ mt: 2 }} onClose={onClose}>
      {feedback.message}
    </Alert>
  );
};

const Profile = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [avatarFeedback, setAvatarFeedback] = useState(emptyBlockFeedback);
  const [basicFeedback, setBasicFeedback] = useState(emptyBlockFeedback);
  const [passportFeedback, setPassportFeedback] = useState(emptyBlockFeedback);
  const [payoutFeedback, setPayoutFeedback] = useState(emptyBlockFeedback);
  const [passwordFeedback, setPasswordFeedback] = useState(emptyBlockFeedback);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [passport, setPassport] = useState(emptyPassport);
  const [passportStatus, setPassportStatus] = useState('not_submitted');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);

  const [payout, setPayout] = useState(emptyPayout);
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' });

  const [savingBasic, setSavingBasic] = useState(false);
  const [savingPassport, setSavingPassport] = useState(false);
  const [savingPayout, setSavingPayout] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const applyUserData = useCallback((data) => {
    setUsername(data.username || '');
    setEmail(data.email || '');
    setPhoneNumber(data.phoneNumber || '');
    setPassport(normalizePassportFromUser(data.passportData));
    setPassportStatus(data.passportVerificationStatus || 'not_submitted');
    setIsEmailVerified(Boolean(data.isEmailVerified));
    setIsPhoneVerified(Boolean(data.isPhoneVerified));
    setAvatarUrl(data.avatar || '');
    setAvatarImgError(false);
  }, []);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const [profileRes, payoutRes] = await Promise.all([
        fetch(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/profile/payout`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const profileData = await profileRes.json().catch(() => ({}));
      if (!profileRes.ok) throw new Error(profileData.message || 'Не удалось загрузить профиль');

      applyUserData(profileData);
      if (setUser) setUser(profileData);

      if (payoutRes.ok) {
        const payoutData = await payoutRes.json().catch(() => ({}));
        setPayout({
          recipientFullName: payoutData.recipientFullName || '',
          phoneForTransfer: payoutData.phoneForTransfer || '',
          cardNumber: payoutData.cardNumber || '',
          banks: payoutData.banks || '',
        });
      }
    } catch (e) {
      console.error(e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [navigate, applyUserData, setUser]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (user) {
      applyUserData(user);
    }

    const loadPayout = async () => {
      try {
        const payoutRes = await fetch(`${API}/api/profile/payout`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (payoutRes.ok) {
          const payoutData = await payoutRes.json().catch(() => ({}));
          setPayout({
            recipientFullName: payoutData.recipientFullName || '',
            phoneForTransfer: payoutData.phoneForTransfer || '',
            cardNumber: payoutData.cardNumber || '',
            banks: payoutData.banks || '',
          });
        }
      } catch {
        // payout optional
      }
    };

    if (user) {
      setLoading(false);
      loadPayout();
      return;
    }

    loadProfile();
  }, [user, loadProfile, applyUserData, navigate]);

  const showBlockFeedback = useCallback((setFeedback, type, message) => {
    setFeedback({ type, message });
    if (type === 'success') {
      setTimeout(() => {
        setFeedback((prev) => (prev.type === 'success' ? emptyBlockFeedback() : prev));
      }, 5000);
    }
  }, []);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    setAvatarUploading(true);
    setAvatarFeedback(emptyBlockFeedback());
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const res = await fetch(`${API}/api/profile/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось загрузить аватар');

      setAvatarUrl(data.avatar || '');
      setAvatarImgError(false);
      if (setUser) setUser((prev) => ({ ...(prev || {}), avatar: data.avatar }));
      showBlockFeedback(setAvatarFeedback, 'success', 'Фото успешно обновлено');
    } catch (e) {
      showBlockFeedback(setAvatarFeedback, 'error', e.message || 'Ошибка загрузки аватара');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveBasic = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setSavingBasic(true);
    setBasicFeedback(emptyBlockFeedback());
    try {
      const res = await fetch(`${API}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: username.trim(),
          phoneNumber: phoneNumber.replace(/\s/g, ''),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить данные');

      const updated = data.user || data;
      applyUserData(updated);
      if (setUser) setUser(updated);
      showBlockFeedback(setBasicFeedback, 'success', 'Данные успешно обновлены');
    } catch (e) {
      showBlockFeedback(setBasicFeedback, 'error', e.message || 'Ошибка сохранения');
    } finally {
      setSavingBasic(false);
    }
  };

  const handleSavePassport = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setSavingPassport(true);
    setPassportFeedback(emptyBlockFeedback());
    try {
      const mergedPassport = {
        ...passport,
        passportSeries: passport.passportSeries.trim(),
        passportNumber: passport.passportNumber.trim(),
        fullName: passport.fullName.trim(),
      };

      const res = await fetch(`${API}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ passportData: mergedPassport }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить паспортные данные');

      const updated = data.user || data;
      applyUserData(updated);
      if (setUser) setUser(updated);
      showBlockFeedback(
        setPassportFeedback,
        'success',
        updated.passportVerificationStatus === 'pending'
          ? 'Паспортные данные сохранены и отправлены на проверку'
          : 'Данные успешно обновлены'
      );
    } catch (e) {
      showBlockFeedback(setPassportFeedback, 'error', e.message || 'Ошибка сохранения');
    } finally {
      setSavingPassport(false);
    }
  };

  const handleSavePayout = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setSavingPayout(true);
    setPayoutFeedback(emptyBlockFeedback());
    try {
      const res = await fetch(`${API}/api/profile/payout`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payout),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сохранить реквизиты');

      setPayout({
        recipientFullName: data.recipientFullName || '',
        phoneForTransfer: data.phoneForTransfer || '',
        cardNumber: data.cardNumber || '',
        banks: data.banks || '',
      });
      showBlockFeedback(setPayoutFeedback, 'success', 'Реквизиты успешно обновлены');
    } catch (e) {
      showBlockFeedback(setPayoutFeedback, 'error', e.message || 'Ошибка сохранения');
    } finally {
      setSavingPayout(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    setSavingPassword(true);
    setPasswordFeedback(emptyBlockFeedback());
    try {
      const res = await fetch(`${API}/api/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(passwords),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Не удалось сменить пароль');

      setPasswords({ oldPassword: '', newPassword: '' });
      showBlockFeedback(setPasswordFeedback, 'success', 'Пароль успешно обновлён');
    } catch (e) {
      showBlockFeedback(setPasswordFeedback, 'error', e.message || 'Ошибка смены пароля');
    } finally {
      setSavingPassword(false);
    }
  };

  const handlePassportChange = (field) => (e) => {
    setPassport((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePayoutChange = (field) => (e) => {
    setPayout((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const userPhotoSrc =
    avatarUrl && !avatarImgError
      ? `${API}/${String(avatarUrl).replace(/^\//, '')}`
      : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: { xs: 10, md: 4 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Мой профиль
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Avatar
            src={userPhotoSrc || BEAR_PAW_AVATAR}
            alt={username || 'Пользователь'}
            imgProps={{
              onError: () => {
                if (avatarUrl && !avatarImgError) setAvatarImgError(true);
              },
            }}
            sx={{
              width: 96,
              height: 96,
              bgcolor: 'rgba(139, 125, 107, 0.15)',
              '& .MuiAvatar-img': {
                objectFit: userPhotoSrc ? 'cover' : 'contain',
                transform: userPhotoSrc ? 'none' : 'scale(0.88)',
              },
            }}
          />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6">{username || 'Пользователь'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={isEmailVerified ? 'Email подтверждён' : 'Email не подтверждён'}
                color={isEmailVerified ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={isPhoneVerified ? 'Телефон подтверждён' : 'Телефон не подтверждён'}
                color={isPhoneVerified ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={PASSPORT_STATUS_LABELS[passportStatus] || passportStatus}
                color={
                  passportStatus === 'approved'
                    ? 'success'
                    : passportStatus === 'pending'
                      ? 'warning'
                      : passportStatus === 'rejected'
                        ? 'error'
                        : 'default'
                }
                variant="outlined"
              />
            </Box>
          </Box>
          <Button
            variant="outlined"
            component="label"
            disabled={avatarUploading}
            sx={{ textTransform: 'none', borderColor: '#8B7D6B', color: '#8B7D6B' }}
          >
            {avatarUploading ? 'Загрузка…' : 'Сменить фото'}
            <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
          </Button>
        </Box>
        <SectionAlert
          feedback={avatarFeedback}
          onClose={() => setAvatarFeedback(emptyBlockFeedback())}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon sx={{ color: '#8B7D6B' }} />
          <Typography variant="h6">Основные данные</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Имя пользователя"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              value={email}
              fullWidth
              size="small"
              disabled
              helperText="Email изменяется через поддержку"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Телефон"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="+79001234567"
            />
          </Grid>
        </Grid>
        <SectionAlert
          feedback={basicFeedback}
          onClose={() => setBasicFeedback(emptyBlockFeedback())}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            sx={brandButtonSx}
            disabled={savingBasic}
            onClick={handleSaveBasic}
          >
            {savingBasic ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Паспортные данные
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Заполните для бронирования туров, аренды и получения выплат как партнёра.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="ФИО (как в паспорте)"
              value={passport.fullName}
              onChange={handlePassportChange('fullName')}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Серия"
              value={passport.passportSeries}
              onChange={handlePassportChange('passportSeries')}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Номер"
              value={passport.passportNumber}
              onChange={handlePassportChange('passportNumber')}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Дата рождения"
              type="date"
              value={passport.dateOfBirth}
              onChange={handlePassportChange('dateOfBirth')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Дата выдачи"
              type="date"
              value={passport.issueDate}
              onChange={handlePassportChange('issueDate')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Кем выдан"
              value={passport.issuedBy}
              onChange={handlePassportChange('issuedBy')}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Адрес регистрации"
              value={passport.registrationAddress}
              onChange={handlePassportChange('registrationAddress')}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
        <SectionAlert
          feedback={passportFeedback}
          onClose={() => setPassportFeedback(emptyBlockFeedback())}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            sx={brandButtonSx}
            disabled={savingPassport}
            onClick={handleSavePassport}
          >
            {savingPassport ? 'Сохранение…' : 'Сохранить паспортные данные'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Реквизиты для выплат
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Укажите, куда переводить заработок от аренды или продажи товаров.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="ФИО получателя"
              value={payout.recipientFullName}
              onChange={handlePayoutChange('recipientFullName')}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Телефон для перевода"
              value={payout.phoneForTransfer}
              onChange={handlePayoutChange('phoneForTransfer')}
              fullWidth
              size="small"
              placeholder="+79001234567"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Номер карты"
              value={payout.cardNumber}
              onChange={handlePayoutChange('cardNumber')}
              fullWidth
              size="small"
              placeholder="При сохранении отображается маской"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Банк(и)"
              value={payout.banks}
              onChange={handlePayoutChange('banks')}
              fullWidth
              size="small"
              placeholder="Сбербанк, Тинькофф…"
            />
          </Grid>
        </Grid>
        <SectionAlert
          feedback={payoutFeedback}
          onClose={() => setPayoutFeedback(emptyBlockFeedback())}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            sx={brandButtonSx}
            disabled={savingPayout}
            onClick={handleSavePayout}
          >
            {savingPayout ? 'Сохранение…' : 'Сохранить реквизиты'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Смена пароля
        </Typography>
        <Box component="form" onSubmit={handleSavePassword}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Текущий пароль"
                type="password"
                value={passwords.oldPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, oldPassword: e.target.value }))}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Новый пароль"
                type="password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
                fullWidth
                size="small"
                required
                inputProps={{ minLength: 6 }}
              />
            </Grid>
          </Grid>
          <SectionAlert
            feedback={passwordFeedback}
            onClose={() => setPasswordFeedback(emptyBlockFeedback())}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              sx={brandButtonSx}
              disabled={savingPassword}
            >
              {savingPassword ? 'Сохранение…' : 'Обновить пароль'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
